/*
 *   File : add-story-slide.js
 *   Author : https://evoqins.com
 *   Description : add story slide component
 *   Version : 1.0.2
 */
import React, { useState, useEffect, useRef } from "react";
import Icon from "../../../Components/icons";
import { CustomFileUpload } from "../../../Components/FormElements";
import { PrimaryButton } from "../../../Components/Buttons";

export default function AddStorySlide({
  errors = [],
  onChange,
  value,
  slides: slidesProp,
  initialSlides,
}) {
  const incoming = value ?? slidesProp ?? initialSlides ?? [];
  const initializedRef = useRef(false);

  const normalizeSlideItem = (item) => {
    if (!item) return { file: null };
    if (typeof item === "string") return { file: item };
    if (item?.file) return { file: item.file };
    if (item?.image_url) return { file: item.image_url };
    if (item?.image) return { file: item.image };
    return { file: null };
  };

  const [slides, setSlides] = useState([{ file: null }]);

  /* -------------------- Prepopulate ONCE -------------------- */
  useEffect(() => {
    if (!initializedRef.current && incoming.length > 0) {
      setSlides(incoming.map(normalizeSlideItem));
      initializedRef.current = true;
    }
  }, [incoming.length]); // 🔑 length only → no loop

  /* -------------------- Send slides to parent -------------------- */
  useEffect(() => {
    onChange && onChange(slides);
  }, [slides]);

  /* -------------------- Add slide -------------------- */
  const addSlide = () => {
    if (slides.length >= 4) return;
    setSlides((prev) => [...prev, { file: null }]);
  };

  /* -------------------- Remove slide -------------------- */
  const removeSlide = (index) => {
    if (slides.length === 1) return;
    setSlides((prev) => prev.filter((_, i) => i !== index));
  };

  /* -------------------- Update file (on select) -------------------- */
  const updateSlide = (index, file) => {
    setSlides((prev) =>
      prev.map((item, i) => (i === index ? { file } : item))
    );
  };

  /* -------------------- Upload complete (URL returned) -------------------- */
  const handleUploadComplete = (index, url) => {
    setSlides((prev) =>
      prev.map((item, i) => (i === index ? { file: url } : item))
    );
  };

  /* -------------------- Clear file -------------------- */
  const clearSlide = (index) => {
    setSlides((prev) =>
      prev.map((item, i) => (i === index ? { file: null } : item))
    );
  };

  return (
    <div className="d-flex flex-column row-gap-3 py-48">
      <div className="d-flex justify-content-between">
        <h3 className="fw-500 fs-18 lh-20 text-eerie-black">
          Add Story Slide
        </h3>
        <p className="mb-0 fw-500 fs-12 lh-24 text-facebook-blue d-flex align-items-center">
          <Icon icon="info" size="14" className="me-1" />
          <span> Max 3 Slides</span>
        </p>
      </div>

      <div className="e-glass-card rounded-16 d-flex flex-column row-gap-3 p-3 mb-2">
        {slides.map((slide, index) => (
          <div key={index} className="d-flex flex-column">
            <div className="d-flex flex-column row-gap-2 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <p className="mb-0 fw-400 fs-14">
                  Slide {index + 1} <span className="text-cg-red">*</span>
                </p>

                {index !== 0 && (
                  <PrimaryButton
                    icon="delete"
                    name="Remove"
                    size="18"
                    ClassName="bg-transparent text-cg-red fs-14 fw-400 px-6 py-0"
                    onPress={() => removeSlide(index)}
                  />
                )}
              </div>

              <CustomFileUpload
                type={2}
                fileSize={3}
                error={errors[index]}
                file={slide.file}
                handleChange={(file) => updateSlide(index, file)}
                handleClear={() => clearSlide(index)}
                onUploadComplete={(url) =>
                  handleUploadComplete(index, url)
                }
              />

              {/* {errors[index] && (
                <div className="text-cg-red fs-12 mt-1">
                  {errors[index]}
                </div>
              )} */}
            </div>
          </div>
        ))}

        {slides.length < 3 && (
          <PrimaryButton
            ClassName="bg-transparent e-w-fit fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
            name="Add slide"
            onPress={addSlide}
          />
        )}
      </div>
    </div>
  );
}
